import { useState } from 'react';

import Card from '@perpay-web/components/base/Card/Card';
import Clickable from '@perpay-web/components/base/Clickable/Clickable';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { useAnimatedModal } from '@perpay-web/components/base/Modal/useAnimatedModal';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import NonFieldErrors from '@perpay-web/components/base/NonFieldErrors/NonFieldErrors';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import Stepper from '@perpay-web/fintech/components/base/Stepper/Stepper';
import IncreasedDirectDepositDrawer from '@perpay-web/fintech/components/composite/IncreasedDirectDepositDrawer/IncreasedDirectDepositDrawer';
import {
    cardApplicationStepperNames,
    REVIEW_DIRECT_DEPOSIT_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { getClassName } from '@perpay-web/utils/domUtils';
import { formatCurrency } from '@perpay-web/utils/stringUtils';

import styles from './CardApplicationSelectMinimumPayment.scss';

const SelectableCard = ({ onSelect, children, isSelected }) => (
    <Clickable
        onClick={onSelect}
        className={getClassName(
            styles.clickable,
            isSelected && styles['selected-card'],
        )}
    >
        <Card className={styles.card}>
            <Stack className={styles.card__stack}>{children}</Stack>
        </Card>
    </Clickable>
);

const CardApplicationSelectMinimumPayment = ({
    totalMinimumPayment,
    onClickSetUpPayment,
    increasedDeposit,
    isSubmitting = false,
    errors,
    onUnmount,
    onClickLearnMore,
    onDirectDepositModalViewed,
    onDirectDepositModalClosed,
}) => {
    useMountAndUnmount(() => onUnmount);
    const { isModalOpen, setIsModalOpen } = useModal();
    const {
        isAnimatedModalOpen,
        setIsAnimatedModalOpen,
        animatedModalClassName,
    } = useAnimatedModal({ isModalOpen, setIsModalOpen });

    const [selectedAmount, setSelectedAmount] = useState(null);

    const totalMinPaymentFormatted = formatCurrency(totalMinimumPayment);
    const increasedDepositFormatted = formatCurrency(increasedDeposit);
    const isMinimumSelected = selectedAmount === totalMinimumPayment;
    const isIncreasedSelected = selectedAmount === increasedDeposit;

    const renderStepper = () => {
        const currentStep = cardApplicationStepperNames.findIndex(
            (step) => step.step === REVIEW_DIRECT_DEPOSIT_STEP,
        );

        return (
            <Stepper
                steps={cardApplicationStepperNames}
                currentStep={currentStep}
                className={getClassName(styles.stepper)}
            />
        );
    };

    return (
        <FocusLayout className={styles.container}>
            {renderStepper()}
            <FocusContent>
                <Stack className={styles.heading}>
                    <div className={styles.header}>
                        Select direct deposit amount
                    </div>
                    <div className={styles.copy}>
                        Choose your preferred amount, log into your payroll
                        system and we’ll take care of the rest.
                    </div>
                </Stack>
                <Stack className={styles.cards__list}>
                    <SelectableCard
                        onSelect={() => {
                            setSelectedAmount(totalMinimumPayment);
                        }}
                        isSelected={isMinimumSelected}
                    >
                        <div className={styles.card__heading}>
                            <span
                                className={getClassName(
                                    styles.title,
                                    isMinimumSelected && styles['blue-text'],
                                )}
                            >
                                Minimum
                            </span>
                            <div
                                className={getClassName(
                                    styles.total,
                                    isMinimumSelected && styles['blue-text'],
                                )}
                            >
                                {totalMinPaymentFormatted}
                                /pay
                            </div>
                        </div>
                    </SelectableCard>
                    <SelectableCard
                        onSelect={() => {
                            setSelectedAmount(increasedDeposit);
                        }}
                        isSelected={isIncreasedSelected}
                    >
                        <div className={styles.card__heading}>
                            <span
                                className={getClassName(
                                    styles.title,
                                    isIncreasedSelected && styles['blue-text'],
                                )}
                            >
                                Higher Direct Deposit
                            </span>
                            <div
                                className={getClassName(
                                    styles.total,
                                    isIncreasedSelected && styles['blue-text'],
                                )}
                            >
                                {increasedDepositFormatted}
                                /pay
                            </div>
                        </div>
                        <Stack className={styles.card__list}>
                            <div className={styles['grey-text']}>
                                <div
                                    className={getClassName(
                                        styles.checks,
                                        isIncreasedSelected &&
                                            styles['selected-checks'],
                                    )}
                                />
                                <div>
                                    <div className={styles.info}>
                                        Can help to earn higher credit limits
                                        faster
                                    </div>
                                </div>
                            </div>
                            <div className={styles['grey-text']}>
                                <div
                                    className={getClassName(
                                        styles.checks,
                                        isIncreasedSelected &&
                                            styles['selected-checks'],
                                    )}
                                />
                                <div>
                                    <div className={styles.info}>
                                        Better for building credit
                                    </div>
                                </div>
                            </div>
                            <div className={styles['grey-text']}>
                                <div
                                    className={getClassName(
                                        styles.checks,
                                        isIncreasedSelected &&
                                            styles['selected-checks'],
                                    )}
                                />
                                <div>
                                    <div className={styles.info}>
                                        Earn more rewards with every payment you
                                        make
                                    </div>
                                </div>
                            </div>
                        </Stack>
                    </SelectableCard>
                </Stack>

                <div className={styles['learn-label']}>
                    <span>
                        <Clickable
                            type='submit'
                            className={styles.learn__more}
                            onClick={() => {
                                onClickLearnMore();
                                setIsAnimatedModalOpen(true);
                            }}
                        >
                            Learn more
                        </Clickable>{' '}
                        about credit limits on the Perpay Credit Card.
                    </span>
                </div>
                {errors ? <NonFieldErrors errors={errors} /> : null}
                {isAnimatedModalOpen ? (
                    <IncreasedDirectDepositDrawer
                        className={animatedModalClassName}
                        onClose={() => {
                            setIsAnimatedModalOpen(false);
                            onDirectDepositModalClosed();
                        }}
                        onMount={onDirectDepositModalViewed}
                    />
                ) : null}
            </FocusContent>
            <FocusFooter>
                <CardCta
                    onClick={() => {
                        onClickSetUpPayment(selectedAmount);
                    }}
                    Component={CustomButton}
                    loading={isSubmitting}
                    disabled={!selectedAmount}
                >
                    Continue
                </CardCta>
            </FocusFooter>
        </FocusLayout>
    );
};

export default CardApplicationSelectMinimumPayment;
